import React from 'react';
import { motion } from 'framer-motion';
import { C } from '../../lib/palette';

// A night sky that fills in over time — one star per consistent day, slowly
// forming a fuller sky. Lines quietly connect the growing shape at later
// stages. Original composition.
const STARS = [
  [58, 214], [80, 190], [70, 162], [96, 152], [112, 172], [122, 140],
  [142, 150], [154, 124], [134, 108], [162, 98], [150, 74], [178, 84],
  [194, 62], [106, 110], [86, 120], [198, 118], [208, 150], [196, 94],
];

export default function ConstellationSky({ activeCount = 0, stage = 1, dormant = false, className = '' }) {
  const shown = Math.min(STARS.length, activeCount);
  const showLines = stage >= 3;

  return (
    <div className={className} data-testid="metaphor-constellation" data-stars={shown} data-dormant={dormant ? '1' : '0'}>
      <svg viewBox="0 0 240 240" width="100%" role="img" aria-label={`Night sky with ${shown} stars`}>
        <defs>
          <linearGradient id="nightSky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#14332C" />
            <stop offset="55%" stopColor="#152A3C" />
            <stop offset="100%" stopColor="#0C1A2E" />
          </linearGradient>
          <radialGradient id="starGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FBF3D6" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#FBF3D6" stopOpacity="0" />
          </radialGradient>
        </defs>

        <rect x="0" y="0" width="240" height="240" rx="14" fill="url(#nightSky)" />

        {/* distant dust */}
        <g fill="#FFFFFF" opacity="0.12">
          {[[30, 40], [210, 36], [46, 96], [220, 200], [36, 180], [190, 210], [120, 30], [66, 60]].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="1" />
          ))}
        </g>

        {/* horizon hill for grounding */}
        <path d="M0 224 Q 120 206 240 224 L240 240 L0 240 Z" fill="#0A1526" opacity="0.8" />

        {/* crescent moon */}
        <g>
          <circle cx="202" cy="44" r="16" fill="#F2E6B8" opacity="0.95" />
          <circle cx="196" cy="40" r="14" fill="#152A3C" />
        </g>

        {/* connecting lines */}
        {showLines && (
          <g stroke="#7FB8A6" strokeWidth="1" strokeLinecap="round" opacity="0.55">
            {STARS.slice(0, shown).map((p, i) => {
              if (i === 0 || i >= shown) return null;
              const prev = STARS[i - 1];
              return <line key={i} x1={prev[0]} y1={prev[1]} x2={p[0]} y2={p[1]} />;
            })}
          </g>
        )}

        {/* stars */}
        {STARS.slice(0, shown).map(([x, y], i) => {
          const big = i % 4 === 0;
          return (
            <g key={i}>
              <circle cx={x} cy={y} r={big ? 9 : 6} fill="url(#starGlow)" />
              <motion.circle
                cx={x}
                cy={y}
                r={big ? 2.6 : 1.9}
                fill="#FBF3D6"
                initial={{ opacity: 0, scale: 0 }}
                animate={dormant ? { opacity: 0.85, scale: 1 } : { opacity: [0.55, 1, 0.55], scale: 1 }}
                transition={dormant ? { duration: 0.6 } : { duration: 2.4 + (i % 3), repeat: Infinity, delay: i * 0.12 }}
              />
            </g>
          );
        })}

        {/* shooting star — stage 5 */}
        {stage >= 5 && !dormant && (
          <motion.line
            x1="40" y1="46" x2="86" y2="70"
            stroke="#FBF3D6" strokeWidth="2" strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: [0, 1, 1], opacity: [0, 1, 0], x: [0, 60], y: [0, 30] }}
            transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 3, ease: 'easeIn' }}
          />
        )}

        {shown === 0 && (
          <text x="120" y="130" textAnchor="middle" fontSize="12" fill="#7E93A8" fontFamily="Fraunces, serif">
            Your first check-in lights the first star
          </text>
        )}
      </svg>
    </div>
  );
}
